// src/features/players/components/AppearanceTab.tsx

import { useRef } from 'react';
import {
  Card, Group, Text, Button, Stack, Box, Select, Switch, TextInput,
  Divider, ColorPicker, ColorSwatch, Popover, ActionIcon, Tooltip, Loader,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconUpload, IconTrash, IconRestore } from '@tabler/icons-react';
import { useForm, useController, type Control } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { notifications } from '@mantine/notifications';
import { modals } from '@mantine/modals';
import { appearanceFormSchema } from '../schemas/PlayerForm.schema';
import { useUpdatePlayer } from '../usePlayerMutations';
import { useUploadOverlay, useDeleteOverlay } from '../usePlayerOverlayMutations';
import { PlayerUtils } from '../PlayerUtils';
import {
  PLAYER_CONSTRAINTS,
  OVERLAY_POSITIONS,
  OVERLAY_POSITION_LABELS,
} from '@/lib/constants/player';
import type { PlayerViewData, PlayerAppearanceFormData } from '@/lib/types/Player';

interface AppearanceTabProps {
  player: PlayerViewData;
}

export function AppearanceTab({ player }: AppearanceTabProps) {
  const playerId = player.core.playerId;
  const updateMutation = useUpdatePlayer(playerId);
  const uploadOverlay = useUploadOverlay(playerId);
  const deleteOverlay = useDeleteOverlay(playerId);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const hasOverlay = player.style.overlayFileId != null;

  const { control, handleSubmit, formState: { isDirty, errors }, reset } = useForm<PlayerAppearanceFormData>({
    resolver: zodResolver(appearanceFormSchema),
    defaultValues: PlayerUtils.transformToAppearanceForm(player),
  });

  const onSubmit = async (data: PlayerAppearanceFormData) => {
    try {
      const req = PlayerUtils.transformAppearanceFormToUpdate(data, player);
      await updateMutation.mutateAsync(req);
      reset(data);
      notifications.show({ title: 'Saved', message: 'Appearance settings updated', color: 'green' });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save changes';
      notifications.show({ title: 'Error', message, color: 'red' });
    }
  };

  const onInvalid = (fieldErrors: typeof errors) => {
    const firstError = Object.values(fieldErrors).find(e => e?.message);
    if (firstError?.message) {
      notifications.show({ title: 'Validation Error', message: firstError.message, color: 'orange' });
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      await uploadOverlay.mutateAsync({ file, contentType: file.type });
      notifications.show({
        title: 'Overlay uploading',
        message: 'Your overlay image is being processed',
        color: 'blue',
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to upload overlay';
      notifications.show({ title: 'Upload Error', message, color: 'red' });
    }

    // Reset file input
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDeleteOverlay = () => {
    modals.openConfirmModal({
      title: 'Delete Overlay?',
      children: (
        <Text size="sm" c="dimmed">
          This will remove the overlay image from this player. This action cannot be undone.
        </Text>
      ),
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await deleteOverlay.mutateAsync();
          notifications.show({ title: 'Deleted', message: 'Overlay removed', color: 'green' });
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Failed to delete overlay';
          notifications.show({ title: 'Error', message, color: 'red' });
        }
      },
    });
  };

  const isPending = updateMutation.isPending;

  return (
    <form onSubmit={handleSubmit(onSubmit, onInvalid)}>
      <Stack gap="xl" maw={800}>
        <Box
          pos="sticky"
          top="calc(var(--app-shell-header-height, 60px) + var(--mantine-spacing-md))"
          bg="white"
          py="sm"
          style={{ zIndex: 10, borderBottom: '1px solid var(--mantine-color-gray-2)' }}
        >
          <Group justify="space-between">
            <Text fw={600} size="lg">Appearance</Text>
            <Group gap="sm">
              {isDirty && (
                <Button variant="default" onClick={() => reset()} disabled={isPending}>
                  Discard
                </Button>
              )}
              <Button type="submit" disabled={!isDirty} loading={isPending}>
                Save Changes
              </Button>
            </Group>
          </Group>
        </Box>

        <Card shadow="sm" padding="lg" withBorder>
          <Stack gap="md">
            {/* Logo Overlay */}
            <Divider label="Logo Overlay" labelPosition="left" />

            {hasOverlay && (
              <Group gap="md" align="center">
                <Text size="sm" c="dimmed">Overlay image is set (ID: {player.style.overlayFileId})</Text>
                <Tooltip label="Delete overlay">
                  <ActionIcon
                    variant="light"
                    color="red"
                    loading={deleteOverlay.isPending}
                    onClick={handleDeleteOverlay}
                  >
                    <IconTrash size={16} />
                  </ActionIcon>
                </Tooltip>
              </Group>
            )}

            <Group gap="sm">
              <Button
                variant="light"
                leftSection={uploadOverlay.isPending ? <Loader size={14} /> : <IconUpload size={14} />}
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadOverlay.isPending}
              >
                {uploadOverlay.isPending ? 'Uploading...' : hasOverlay ? 'Replace Overlay' : 'Upload Overlay'}
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/gif,image/webp"
                style={{ display: 'none' }}
                onChange={handleFileSelect}
              />
            </Group>

            {hasOverlay && (
              <>
                <OverlayPositionField control={control} />
                <OverlayClickUrlField control={control} />
              </>
            )}

            {/* Playlist */}
            <Divider label="Playlist" labelPosition="left" />
            <SwitchField control={control} name="plShowDescShort" label="Show Description" />
            <SwitchField control={control} name="plShowDate" label="Show Date" />
            <PlaylistBarLabelField control={control} />
            <Group grow>
              <PlaylistBarColorField control={control} name="plBarBgcolor" label="Bar Background" defaultColor="#4281FF" />
              <PlaylistBarColorField control={control} name="plBarFgcolor" label="Bar Text Color" defaultColor="#FFFFFF" />
            </Group>
          </Stack>
        </Card>
      </Stack>
    </form>
  );
}

// --- Field components ---

function OverlayPositionField({ control }: { control: Control<PlayerAppearanceFormData> }) {
  const { field } = useController({ control, name: 'overlayPosition' });
  return (
    <Select
      label="Overlay Position"
      value={field.value}
      onChange={(val) => val && field.onChange(val)}
      data={OVERLAY_POSITIONS.map(p => ({ value: p, label: OVERLAY_POSITION_LABELS[p] }))}
      w={200}
    />
  );
}

function OverlayClickUrlField({ control }: { control: Control<PlayerAppearanceFormData> }) {
  const { field, fieldState } = useController({ control, name: 'overlayClickUrl' });
  return (
    <TextInput
      label="Overlay Click URL"
      placeholder="https://example.com"
      description={`Leave empty for no click action (${PLAYER_CONSTRAINTS.OVERLAY_CLICK_URL_MIN}-${PLAYER_CONSTRAINTS.OVERLAY_CLICK_URL_MAX} chars if set)`}
      value={field.value}
      onChange={field.onChange}
      onBlur={field.onBlur}
      error={fieldState.error?.message}
      maxLength={PLAYER_CONSTRAINTS.OVERLAY_CLICK_URL_MAX}
    />
  );
}

function PlaylistBarLabelField({ control }: { control: Control<PlayerAppearanceFormData> }) {
  const { field, fieldState } = useController({ control, name: 'plBarLabel' });
  return (
    <TextInput
      label="Playlist Bar Label"
      value={field.value}
      onChange={field.onChange}
      onBlur={field.onBlur}
      error={fieldState.error?.message}
      description={`${field.value.length}/${PLAYER_CONSTRAINTS.PLAYLIST_BAR_LABEL_MAX}`}
      maxLength={PLAYER_CONSTRAINTS.PLAYLIST_BAR_LABEL_MAX}
      w={200}
    />
  );
}

/** Swatch-based color picker: shows a labeled color swatch, click to open popover with full picker + reset */
function PlaylistBarColorField({ control, name, label, defaultColor }: {
  control: Control<PlayerAppearanceFormData>;
  name: 'plBarBgcolor' | 'plBarFgcolor';
  label: string;
  defaultColor: string;
}) {
  const { field } = useController({ control, name });
  const [opened, { open, close }] = useDisclosure(false);
  const displayColor = field.value || defaultColor;

  return (
    <Stack gap={4}>
      <Text size="sm" fw={500}>{label}</Text>
      <Popover opened={opened} onClose={close} position="bottom-start" shadow="md" withArrow>
        <Popover.Target>
          <ColorSwatch
            color={displayColor}
            size={32}
            onClick={open}
            style={{ cursor: 'pointer', border: '1px solid var(--mantine-color-gray-3)' }}
          />
        </Popover.Target>
        <Popover.Dropdown>
          <Stack gap="sm">
            <ColorPicker
              format="hex"
              value={displayColor}
              onChange={field.onChange}
            />
            <TextInput
              size="xs"
              value={field.value}
              onChange={(e) => field.onChange(e.currentTarget.value)}
              placeholder={defaultColor}
              leftSection={<ColorSwatch color={displayColor} size={14} />}
            />
            <Button
              variant="subtle"
              size="xs"
              leftSection={<IconRestore size={14} />}
              onClick={() => { field.onChange(defaultColor); close(); }}
            >
              Reset to default
            </Button>
          </Stack>
        </Popover.Dropdown>
      </Popover>
    </Stack>
  );
}

function SwitchField({ control, name, label }: {
  control: Control<PlayerAppearanceFormData>; name: keyof PlayerAppearanceFormData; label: string;
}) {
  const { field } = useController({ control, name });
  return (
    <Switch
      label={label}
      checked={field.value as boolean}
      onChange={(e) => field.onChange(e.currentTarget.checked)}
    />
  );
}
